/**
 * Candidates a call outcome took out of the working lists.
 *
 * The other half of the destructive outcomes. Wrong Profile, Not Suitable and
 * Invalid Number were specified as "delete the data"; they clear
 * `is_active` instead, and without a screen that reads those rows back,
 * "nothing was deleted" would be a claim nobody could check.
 *
 * Company admin and above, matching the server. A recruiter putting back a
 * candidate a colleague retired is the argument this avoids having twice.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../../api/client';
import { formatPhone } from '../../lib/phone';
import { titleCaseName } from '../../lib/titleCase';
import { useDebounced } from '../../lib/useDebounced';
import type { Candidate, Page } from '../../types';

export default function RetiredCandidatesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const term = useDebounced(search);

  const retired = useQuery<Page<Candidate>>({
    queryKey: ['candidates', 'retired', term],
    queryFn: async () =>
      (
        await client.get('/api/v1/candidates/retired', {
          params: { limit: 200, ...(term.trim() ? { search: term.trim() } : {}) },
        })
      ).data,
  });

  const restore = useMutation({
    mutationFn: async (id: string) =>
      (await client.post(`/api/v1/candidates/${id}/restore`)).data as Candidate,
    onSuccess: (back) => {
      // Both lists move: this one loses a row and the working list gains one.
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      toast.success(`${titleCaseName(back.fullName) ?? 'Candidate'} is back`);
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not restore that candidate')),
  });

  const rows = retired.data?.items ?? [];

  return (
    <div className="space-y-4">
      <section className="card">
        <h2 className="text-sm font-semibold text-gray-900">Retired candidates</h2>
        <p className="mt-1 max-w-2xl text-xs text-gray-500">
          Taken out of the working lists by a call outcome — Wrong Profile, Not
          Suitable or Invalid Number. Nothing was deleted: their calls, their
          history and their number are all still here, and restoring one puts
          it back on the Daily Calls Report and every picker.
        </p>

        <label className="mt-3 block max-w-sm text-xs text-gray-600">
          Search
          <div className="relative mt-1">
            <Search className="absolute top-2.5 left-3 h-4 w-4 text-gray-400" />
            <input
              type="search"
              className="input pl-9"
              placeholder="Name or mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </label>
      </section>

      {retired.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2.5 font-medium">Candidate</th>
                <th className="px-4 py-2.5 font-medium">Mobile</th>
                <th className="px-4 py-2.5 font-medium">Qualification</th>
                <th className="px-4 py-2.5 font-medium">
                  <span className="sr-only">Restore</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900">
                    {titleCaseName(row.fullName) ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                    {formatPhone(row.phone) ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    {row.qualification ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      disabled={restore.isPending}
                      onClick={() => restore.mutate(row.id)}
                      title="Put this candidate back into the working lists"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Restore
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                  {term.trim()
                    ? 'Nobody retired matches that.'
                    : 'Nobody has been retired.'}
                </td>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
